import * as React from "react";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Container } from "react-bootstrap";
import { observer } from "mobx-react";
import { runInAction } from "mobx";
import moment from "moment-timezone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarAlt, faCheckCircle, faClock, faMapMarkerAlt } from "@fortawesome/free-solid-svg-icons";
import {
  bookCoinRental,
  CoinRentalRoom,
  CoinRentalTimeSlot,
  CoinRentType,
  getCoinRentalAvailability,
  getCoinRentalRooms,
  getCoinRentalTypes,
  getUserUpcomingRentals,
  UpcomingRental
} from "../../../api/API";
import { RootStoreContext } from "../../../stores/RootStore";
import { StepName } from "../../../config/Wizard";
import { FULL_PAGE } from "../../SpinnerWrapper";
import ConfirmButton from "./ConfirmButton";
import DateSelector from "./DateSelector";
import RoomCarousel from "./RoomCarousel";
import UseTypeInfoModal from "./UseTypeInfoModal";
import "./styles.css";

interface RentalSectionProps {
  rentalServices: any[];
}

interface RequestIdentity {
  selectionKey: string;
  requestId: string;
}

const rentalDate = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const requestId = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, character => {
    const random = Math.floor(Math.random() * 16);
    const value = character === "x" ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
};

const RentalSection: React.FC<RentalSectionProps> = observer(() => {
  const { studioConfigStore, registrationStore, spinnerStore, wizardStore } =
    useContext(RootStoreContext);

  const selectedDate = studioConfigStore.selectedDate;
  const dateString = rentalDate(selectedDate);
  const [rooms, setRooms] = useState<CoinRentalRoom[]>([]);
  const [selectedRoom, setSelectedRoom] = useState<CoinRentalRoom | null>(null);
  const [rentTypes, setRentTypes] = useState<CoinRentType[]>([]);
  const [roomRentTypes, setRoomRentTypes] = useState<CoinRentType[]>([]);
  const [selectedRentType, setSelectedRentType] = useState<CoinRentType | null>(null);
  const [timeSlots, setTimeSlots] = useState<CoinRentalTimeSlot[]>([]);
  const [selectedSlots, setSelectedSlots] = useState<string[]>([]);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [loadingRentTypes, setLoadingRentTypes] = useState(false);
  const [loadingRoomRentTypes, setLoadingRoomRentTypes] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showUseTypeInfo, setShowUseTypeInfo] = useState(false);
  const [bookingError, setBookingError] = useState<string | null>(null);
  const [upcomingRentals, setUpcomingRentals] = useState<UpcomingRental[]>([]);
  const [loadingUpcomingRentals, setLoadingUpcomingRentals] = useState(true);
  const [bookingFlowStarted, setBookingFlowStarted] = useState(false);
  const requestIdentityRef = useRef<RequestIdentity | null>(null);
  const availabilityRequestRef = useRef(0);
  const selectionRevisionRef = useRef(0);

  const startBookingFlow = useCallback(() => {
    setBookingFlowStarted(true);
  }, []);

  const loadUpcomingRentals = useCallback(async () => {
    if (!registrationStore.user?.id) {
      setUpcomingRentals([]);
      setLoadingUpcomingRentals(false);
      return;
    }
    setLoadingUpcomingRentals(true);
    try {
      setUpcomingRentals(await getUserUpcomingRentals(registrationStore.user.id));
    } catch {
      setUpcomingRentals([]);
    } finally {
      setLoadingUpcomingRentals(false);
    }
  }, [registrationStore.user?.id]);

  useEffect(() => {
    loadUpcomingRentals();
  }, [loadUpcomingRentals]);

  useEffect(() => {
    let current = true;
    selectionRevisionRef.current += 1;
    setLoadingRooms(true);
    setBookingError(null);
    setSelectedSlots([]);
    setRoomRentTypes([]);
    setTimeSlots([]);
    requestIdentityRef.current = null;
    availabilityRequestRef.current += 1;

    getCoinRentalRooms(dateString)
      .then(data => {
        if (!current) return;

        setRooms(data);
        setSelectedRoom(previous => {
          const preservedRoom = data.find(room => room.id === previous?.id);
          if (preservedRoom) return preservedRoom;
          return data[0] || null;
        });
      })
      .catch(() => {
        if (current) {
          setRooms([]);
          setSelectedRoom(null);
          setBookingError("Failed to load rooms. Please try again.");
        }
      })
      .finally(() => {
        if (current) setLoadingRooms(false);
      });

    return () => {
      current = false;
    };
  }, [dateString]);

  useEffect(() => {
    let current = true;
    setLoadingRentTypes(true);

    getCoinRentalTypes(dateString)
      .then(data => {
        if (!current) return;

        setRentTypes(data);
        setSelectedRentType(previous =>
          data.find(rentType => rentType.id === previous?.id) || data[0] || null
        );
      })
      .catch(() => {
        if (current) {
          setRentTypes([]);
          setSelectedRentType(null);
          setBookingError("Failed to load use types. Please try again.");
        }
      })
      .finally(() => {
        if (current) setLoadingRentTypes(false);
      });

    return () => {
      current = false;
    };
  }, [dateString]);

  useEffect(() => {
    if (!selectedRoom) {
      setRoomRentTypes([]);
      setLoadingRoomRentTypes(false);
      return;
    }
    let current = true;
    setLoadingRoomRentTypes(true);
    setSelectedSlots([]);
    setTimeSlots([]);
    requestIdentityRef.current = null;
    availabilityRequestRef.current += 1;

    getCoinRentalTypes(dateString, selectedRoom.id)
      .then(data => {
        if (!current) return;

        setRoomRentTypes(data);
      })
      .catch(() => {
        if (current) {
          setRoomRentTypes([]);
          setBookingError("Failed to check use types for this room. Please try again.");
        }
      })
      .finally(() => {
        if (current) setLoadingRoomRentTypes(false);
      });

    return () => {
      current = false;
    };
  }, [dateString, selectedRoom]);

  const selectedTypeAvailableForRoom = Boolean(
    selectedRentType && roomRentTypes.some(rentType => rentType.id === selectedRentType.id)
  );

  const loadAvailability = useCallback(async () => {
    const requestVersion = ++availabilityRequestRef.current;
    if (!selectedRoom || !selectedRentType || !selectedTypeAvailableForRoom) {
      setLoadingSlots(false);
      return;
    }

    setLoadingSlots(true);
    try {
      const data = await getCoinRentalAvailability(
        dateString,
        selectedRoom.id,
        selectedRentType.id
      );
      if (requestVersion !== availabilityRequestRef.current) return;

      setTimeSlots(data);
      setSelectedSlots(current =>
        current.filter(start => data.some(slot => slot.start === start && slot.available))
      );
    } catch {
      if (requestVersion !== availabilityRequestRef.current) return;

      setTimeSlots([]);
      setSelectedSlots([]);
      setBookingError("Failed to load available times. Please try again.");
    } finally {
      if (requestVersion === availabilityRequestRef.current) {
        setLoadingSlots(false);
      }
    }
  }, [dateString, selectedRoom, selectedRentType, selectedTypeAvailableForRoom]);

  useEffect(() => {
    setSelectedSlots([]);
    requestIdentityRef.current = null;
    loadAvailability();
  }, [loadAvailability]);

  const selectedSlotRecords = useMemo(
    () => timeSlots.filter(slot => selectedSlots.includes(slot.start)),
    [selectedSlots, timeSlots]
  );
  const totalCoins = selectedSlotRecords.reduce((sum, slot) => sum + slot.coins30min, 0);
  const availableCoins =
    registrationStore.user?.latestTrainingPackage?.availableCoins ?? 0;
  const hasEnoughCoins = totalCoins <= availableCoins;

  const toggleSlot = (slot: CoinRentalTimeSlot) => {
    if (submitting || !slot.available) return;
    selectionRevisionRef.current += 1;
    setBookingError(null);
    setSelectedSlots(current =>
      current.includes(slot.start)
        ? current.filter(start => start !== slot.start)
        : [...current, slot.start].sort()
    );
    requestIdentityRef.current = null;
  };

  const handleConfirm = async () => {
    if (submitting || !selectedRoom || !selectedRentType || selectedSlots.length === 0) return;

    const selectionKey = JSON.stringify({
      userId: registrationStore.user.id,
      locationId: selectedRoom.id,
      date: dateString,
      rentTypeId: selectedRentType.id,
      slots: selectedSlots
    });
    const submittedRevision = selectionRevisionRef.current;
    if (requestIdentityRef.current?.selectionKey !== selectionKey) {
      requestIdentityRef.current = { selectionKey, requestId: requestId() };
    }

    setBookingError(null);
    setSubmitting(true);
    spinnerStore.show(FULL_PAGE);
    let refreshAvailability = false;
    try {
      const result = await bookCoinRental({
        userId: registrationStore.user.id,
        locationId: selectedRoom.id,
        date: dateString,
        rentTypeId: selectedRentType.id,
        slots: selectedSlots,
        requestId: requestIdentityRef.current.requestId
      });

      const groups = selectedSlotRecords
        .slice()
        .sort((left, right) => left.start.localeCompare(right.start))
        .reduce<CoinRentalTimeSlot[][]>((resultGroups, slot) => {
          const currentGroup = resultGroups[resultGroups.length - 1];
          if (!currentGroup || currentGroup[currentGroup.length - 1].end !== slot.start) {
            resultGroups.push([slot]);
          } else {
            currentGroup.push(slot);
          }
          return resultGroups;
        }, []);

      runInAction(() => {
        if (registrationStore.user.latestTrainingPackage) {
          registrationStore.user.latestTrainingPackage.availableCoins = result.remainingCoins;
        }
        registrationStore.selectedRentals = groups.map(group => ({
          rentalName: result.rentTypeName || selectedRentType.name,
          locationName: result.locationName || selectedRoom.name,
          venueName: selectedRoom.venueName || "",
          venueAddress: "",
          startTime: moment.tz(
            `${dateString} ${group[0].start}`,
            "YYYY-MM-DD HH:mm",
            "Europe/Tallinn"
          ).toISOString(),
          endTime: moment.tz(
            `${dateString} ${group[group.length - 1].end}`,
            "YYYY-MM-DD HH:mm",
            "Europe/Tallinn"
          ).toISOString(),
          date: moment(dateString).format("ddd, MMM D")
        }));
      });

      await loadUpcomingRentals();
      wizardStore.setStep(StepName.REGISTRATION_SUCCESS);
    } catch (error: any) {
      if (submittedRevision !== selectionRevisionRef.current) return;

      if (error?.code === "NOT_ENOUGH_COINS") {
        setBookingError("You don’t have enough coins for this booking.");
      } else if (error?.code === "TIME_SLOT_UNAVAILABLE") {
        setBookingError("This time slot is no longer available. Please choose another time.");
        setSelectedSlots([]);
        requestIdentityRef.current = null;
        refreshAvailability = true;
      } else if (
        error?.code === "COIN_PRICE_UNAVAILABLE" ||
        error?.code === "COIN_PRICING_AMBIGUOUS"
      ) {
        setBookingError("Coin pricing changed for this booking. Please choose your time again.");
        setSelectedSlots([]);
        requestIdentityRef.current = null;
        refreshAvailability = true;
      } else if (error?.code === "IDEMPOTENCY_KEY_REUSED") {
        setBookingError("This booking selection changed. Please confirm it again.");
        requestIdentityRef.current = null;
      } else {
        setBookingError(error?.message || "Failed to book the rental. Please try again.");
      }
      if (refreshAvailability) {
        await loadAvailability();
      }
    } finally {
      setSubmitting(false);
      spinnerStore.hide(FULL_PAGE);
    }
  };

  const formatUpcoming = (rental: UpcomingRental) => {
    const start = moment(rental.startTime).tz("Europe/Tallinn");
    const end = moment(rental.endTime).tz("Europe/Tallinn");
    return {
      date: start.format("ddd, MMM D"),
      time: `${start.format("HH:mm")} - ${end.format("HH:mm")}`
    };
  };

  return (
    <Container>
      {!loadingUpcomingRentals && upcomingRentals.length === 0 &&
        studioConfigStore.roomRentalDescription && (
          <p className="room-rental-description">
            {studioConfigStore.roomRentalDescription}
          </p>
        )}

      {upcomingRentals.length > 0 && !bookingFlowStarted && (
        <div className="item-container" style={{ marginBottom: "20px" }}>
          <div className="detail-card">
            <div className="detail-card-header">
              <FontAwesomeIcon icon={faCheckCircle} className="section-icon" />
              <h4 className="detail-title">Your upcoming room rentals</h4>
            </div>
            <div className="detail-content">
              {upcomingRentals.map((rental, index) => {
                const formatted = formatUpcoming(rental);
                return (
                  <div className="coin-upcoming-rental" key={`${rental.startTime}-${index}`}>
                    <strong>{rental.locationName}</strong>
                    <span><FontAwesomeIcon icon={faCalendarAlt} /> {formatted.date}</span>
                    <span><FontAwesomeIcon icon={faClock} /> {formatted.time}</span>
                    <span><FontAwesomeIcon icon={faMapMarkerAlt} /> {rental.venueName}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <DateSelector
        onDateSelect={date => {
          if (submitting) return;
          selectionRevisionRef.current += 1;
          startBookingFlow();
          setSelectedRoom(current => current || rooms[0] || null);
          studioConfigStore.setSelectedDate(date);
        }}
        checkInFutureDays={studioConfigStore.rentFutureDays}
        showDiscountPromo={false}
        disabled={submitting}
        displayMode="calendar"
      />

      <div className="coin-rental-section">
        <h4>Choose a room</h4>
        {loadingRooms && <p>Loading rooms…</p>}
        {!loadingRooms && rooms.length === 0 && (
          <p className="coin-rental-empty">No rooms are available for coins on this date.</p>
        )}
        <RoomCarousel
          rooms={rooms}
          selectedRoom={selectedRoom}
          disabled={submitting}
          onSelect={room => {
            if (selectedRoom?.id === room.id) return;
            selectionRevisionRef.current += 1;
            startBookingFlow();
            setBookingError(null);
            setSelectedRoom(room);
            setLoadingRoomRentTypes(true);
            setSelectedSlots([]);
            setRoomRentTypes([]);
            setTimeSlots([]);
            requestIdentityRef.current = null;
            availabilityRequestRef.current += 1;
          }}
        />
      </div>

      <div className="coin-rental-section">
        <h4 className="use-type-heading">
          <span>Select use type</span>
          <button
            type="button"
            className="use-type-info-button"
            aria-label="About use types"
            title="About use types"
            onClick={() => setShowUseTypeInfo(true)}
          >
            ℹ️
          </button>
        </h4>
        {loadingRentTypes && <p>Loading use types…</p>}
        {!loadingRentTypes && rentTypes.length === 0 && (
          <p className="coin-rental-empty">
            No use types are configured for coins on this date.
          </p>
        )}
        <div className="coin-rent-type-grid">
          {rentTypes.map(rentType => (
            <button
              type="button"
              disabled={submitting}
              key={rentType.id}
              title={rentType.description}
              className={selectedRentType?.id === rentType.id ? "active" : ""}
              onClick={() => {
                if (selectedRentType?.id === rentType.id) return;
                selectionRevisionRef.current += 1;
                setBookingError(null);
                setSelectedRentType(rentType);
                setSelectedSlots([]);
                setTimeSlots([]);
                requestIdentityRef.current = null;
                availabilityRequestRef.current += 1;
              }}
            >
              {rentType.name}
            </button>
          ))}
        </div>
        {selectedRoom && loadingRoomRentTypes && <p>Checking room availability…</p>}
        {selectedRoom && selectedRentType && !loadingRoomRentTypes &&
          !selectedTypeAvailableForRoom && (
            <p className="coin-rental-empty">
              This use type is not available for coins in the selected room.
            </p>
          )}
      </div>

      <UseTypeInfoModal
        show={showUseTypeInfo}
        onHide={() => setShowUseTypeInfo(false)}
        rentTypes={rentTypes}
        loading={loadingRentTypes}
      />

      {selectedRoom && selectedRentType && selectedTypeAvailableForRoom && (
        <div className="coin-rental-section">
          <h4>Select time slots <small>(30 min each)</small></h4>
          <p className="coin-slot-date">
            <time dateTime={dateString}>
              {moment.tz(dateString, "YYYY-MM-DD", "Europe/Tallinn").format("dddd, MMMM D, YYYY")}
            </time>
          </p>
          {loadingSlots && <p>Loading available times…</p>}
          {!loadingSlots && timeSlots.length === 0 && (
            <p className="coin-rental-empty">
              There are no coin-priced time slots for this selection.
            </p>
          )}
          <div className="coin-slot-grid">
            {timeSlots.map(slot => (
              <button
                type="button"
                key={slot.start}
                disabled={submitting || !slot.available}
                className={[
                  selectedSlots.includes(slot.start) ? "active" : "",
                  !slot.available ? "unavailable" : ""
                ].filter(Boolean).join(" ")}
                onClick={() => toggleSlot(slot)}
              >
                <span>{slot.start}–{slot.end}</span>
                <strong>Ȼ{slot.coins30min}</strong>
              </button>
            ))}
          </div>
        </div>
      )}

      {selectedSlots.length > 0 && (
        <div className="coin-rental-summary">
          <span>{selectedSlots.length} × 30-minute slot{selectedSlots.length === 1 ? "" : "s"}</span>
          <strong>Total Ȼ{totalCoins}</strong>
          <small>
            Available balance: Ȼ{availableCoins}
          </small>
          {!hasEnoughCoins && (
            <small className="coin-rental-insufficient">
              You don’t have enough coins for this booking.
            </small>
          )}
        </div>
      )}

      {bookingError && <div className="alert alert-danger">{bookingError}</div>}

      {selectedRoom && selectedRentType && selectedTypeAvailableForRoom && (
        <>
          <ConfirmButton
            hasRentalSelected={true}
            onRentalConfirm={handleConfirm}
            disabled={submitting || selectedSlots.length === 0 || !hasEnoughCoins}
          />
          <br />
          <br />
        </>
      )}
    </Container>
  );
});

export default RentalSection;
